import React from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as GraphExploreIcon } from "assets/icon-graph-explorer.svg";

/**
 * Props for the GraphExploreButton component.
 * @property variant - "icon" shows only the network icon; "text" shows icon + text in flex-row.
 * @property text - Label displayed when variant is "text". Defaults to "Explore".
 * @property onClick - Optional custom click handler. When omitted, navigates to /graph.
 * @property className - Additional CSS classes for the button.
 * @property ariaLabel - Accessible label for screen readers.
 * @property title - Tooltip text on hover.
 */
interface GraphExploreButtonProps {
  variant?: "icon" | "text";
  text?: string;
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
  title?: string;
}

/**
 * Reusable button to navigate to the Graph Explorer (/graph).
 * Supports icon-only or icon+text variants, with optional custom action override.
 */
const GraphExploreButton: React.FC<GraphExploreButtonProps> = ({
  variant = "icon",
  text = "Explore",
  onClick,
  className = "",
  ariaLabel,
  title = "Explore issue graph",
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate("/graph");
    }
  };

  const resolvedAriaLabel = ariaLabel ?? (variant === "icon" ? "Explore issue graph" : text);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`graph-explore-button flex flex-row items-center justify-center gap-2 rounded-full p-1.5 md:p-2 text-eoe-gray dark:text-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover transition-colors ${className}`}
      aria-label={resolvedAriaLabel}
      title={title}
    >
      <GraphExploreIcon className="size-6 shrink-0" aria-hidden />
      {variant === "text" && (
        <span className="graph-explore-button-text text-sm font-semibold whitespace-nowrap">
          {text}
        </span>
      )}
    </button>
  );
};

export default GraphExploreButton;
